import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Body1, Body1Strong, Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { SendRegular, DismissCircleRegular, AddRegular, SparkleRegular } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Chart, ChartSuggestionsData, EditChartSuggestionItem, NewChartSuggestionItem } from '../../api/types';
import { streamAiEdit, AiEditRequest, AiEditHandlers, AiEditSuggestion, getChatHistory } from '../../api';
import { chartsService } from '../../api/chartsService';

interface AIEditorProps {
  chart: Chart;
  previewChart?: Chart | null;
  onPreviewUpdate?: (previewKey: string, reason: string) => void;
  /** True when creating a new chart (Add Chart flow) */
  isNewChart?: boolean;
  /** Dashboard ID — used for lazy Redis preview creation */
  dashboardId?: string;
  /** Latest Redis preview key from visual editor changes (preview-update).
   *  When set, AI edit will use this as context instead of the DB chart. */
  previewChartId?: string | null;
  /** Pre-loaded chart suggestions from dashboard (new chart flow) */
  chartSuggestions?: ChartSuggestionsData | null;
  /** Per-chart edit suggestions (edit chart flow) */
  editSuggestions?: EditChartSuggestionItem[] | null;
  /** Active dashboard filters — passed to explain skill for filtered data context */
  filters?: Record<string, unknown>;
}

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  shouldType?: boolean;
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },

  chatArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  messageRow: {
    display: 'flex',
    width: '100%',
  },

  assistantMessage: {
    maxWidth: '85%',
    color: tokens.colorNeutralForeground1,
    '& p': {
      margin: '0 0 8px 0',
      lineHeight: '1.5',
    },
    '& p:last-child': {
      marginBottom: 0,
    },
    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '24px',
    },
    '& li': {
      marginBottom: '6px',
      lineHeight: '1.5',
    },
    '& li > p': {
      margin: 0,
    },
    '& li:last-child': {
      marginBottom: 0,
    },
    '& code': {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: '2px 4px',
      borderRadius: '3px',
      fontSize: '0.9em',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: '12px',
      borderRadius: '6px',
      overflow: 'auto',
      margin: '8px 0',
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '12px 0 8px 0',
      fontWeight: 600,
    },
    '& h1': {
      fontSize: tokens.fontSizeBase500,
    },
    '& h2': {
      fontSize: tokens.fontSizeBase400,
    },
    '& h3': {
      fontSize: tokens.fontSizeBase300,
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
      margin: '12px 0 8px 0',
    },
    '& blockquote': {
      borderLeft: `3px solid ${tokens.colorNeutralStroke2}`,
      paddingLeft: '12px',
      margin: '8px 0',
      color: tokens.colorNeutralForeground2,
    },
    '& strong': {
      fontWeight: 600,
    },
    '& em': {
      fontStyle: 'italic',
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '8px 0',
      fontSize: '0.9em',
    },
    '& th, & td': {
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      padding: '6px 10px',
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: 600,
    },
    '& tr:nth-child(even)': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },

  userMessageRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    paddingTop: '12px',
    marginTop: '4px',
  },

  userBubble: {
    maxWidth: '70%',
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    padding: '8px 12px',
    borderRadius: '12px',
  },

  composerWrapper: {
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
  },

  composerSurface: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    padding: '10px 10px 10px 12px',
    borderRadius: '14px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },

  textarea: {
    flex: 1,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    padding: 0,
    margin: 0,
    resize: 'none',
    minHeight: '22px',
    maxHeight: '120px',
    overflowY: 'auto',
  },

  sendButton: {
    width: '22px',
    height: '22px',
    minWidth: '22px',
    padding: 0,
    borderRadius: '6px',
    alignSelf: 'flex-end',
    color: tokens.colorBrandForeground1,
    ':hover': {
      color: tokens.colorBrandForeground2Hover,
    },
    ':active': {
      color: tokens.colorBrandForeground2Pressed,
    },
    ':disabled': {
      color: tokens.colorNeutralForegroundDisabled,
    },
  },

  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralForeground3,
    animationName: {
      from: {
        transform: 'scale(0.6)',
        opacity: 0.4,
      },
      to: {
        transform: 'scale(1)',
        opacity: 1,
      },
    },
    animationDuration: '0.6s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    animationDirection: 'alternate',
  },

  dotDelay1: {
    animationDelay: '0s',
  },

  dotDelay2: {
    animationDelay: '0.2s',
  },

  dotDelay3: {
    animationDelay: '0.4s',
  },

  progressText: {
    fontSize: tokens.fontSizeBase300,
    fontStyle: 'italic',
    fontWeight: 400,
    background: `linear-gradient(90deg, 
      ${tokens.colorNeutralForeground4} 0%, 
      ${tokens.colorNeutralForeground1} 40%, 
      ${tokens.colorNeutralForeground1} 60%, 
      ${tokens.colorNeutralForeground4} 100%)`,
    backgroundSize: '200% 100%',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animationName: {
      '0%': { 
        backgroundPosition: '200% 0',
        opacity: 0.6 
      },
      '50%': { 
        opacity: 1 
      },
      '100%': { 
        backgroundPosition: '-200% 0',
        opacity: 0.6 
      },
    },
    animationDuration: '2.5s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },

  // ── Suggestion cards ──────────────────────────────────────────────────

  suggestionsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '4px 0',
  },

  suggestionsIntro: {
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.4',
    marginBottom: '4px',
  },

  suggestionCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    transition: 'background-color 0.15s, border-color 0.15s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
    },
  },

  suggestionContent: {
    flex: 1,
    minWidth: 0,
  },

  suggestionTitle: {
    fontWeight: 600,
    fontSize: tokens.fontSizeBase300,
    lineHeight: '1.3',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  suggestionDescription: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: '1.3',
    color: tokens.colorNeutralForeground3,
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginTop: '2px',
  },

  suggestionAddButton: {
    minWidth: '28px',
    width: '28px',
    height: '28px',
    padding: 0,
    flexShrink: 0,
  },

  showSuggestionsButton: {
    alignSelf: 'flex-start',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForeground1,
    padding: '4px 8px',
    minHeight: 'auto',
    ':hover': {
      color: tokens.colorBrandForeground2Hover,
    },
  },
});


/** Fix markdown where numbered list items have the title on a new line */
function fixMarkdownLists(text: string): string {
  // "1. \nTitle" or "1.\nTitle" → "1. Title"
  return text.replace(/^(\d+\.)\s*\n\s*/gm, '$1 ');
}

const AIEditor: React.FC<AIEditorProps> = ({ chart, onPreviewUpdate, isNewChart, dashboardId, previewChartId, chartSuggestions, editSuggestions, filters }) => {
  const styles = useStyles();
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortStreamRef = useRef<(() => void) | null>(null);
  const messageIdCounter = useRef(0);
  const fullBufferRef = useRef<string>('');
  const visibleBufferRef = useRef<string>('');

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [promptText, setPromptText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleBuffer, setVisibleBuffer] = useState('');
  const [systemMessage, setSystemMessage] = useState('');
  const [, setHistoryLoaded] = useState(false);

  // Redis preview key — for new charts created lazily on first AI interaction,
  // for existing charts synced from the parent's visual editor preview-update calls.
  const previewKeyRef = useRef<string | null>(previewChartId ?? null);

  // Keep previewKeyRef in sync with parent's previewChartId and reset on chart switch
  useEffect(() => {
    previewKeyRef.current = previewChartId ?? null;
  }, [chart.id, previewChartId]);

  /**
   * Ensures a Redis preview exists for the Add Chart flow.
   * Creates one lazily on the first AI interaction and caches it.
   * For existing charts returns the latest preview key from visual editor (if any).
   */
  const ensurePreviewKey = useCallback(async (): Promise<string | null> => {
    // For existing charts, return the latest preview key from visual editor changes
    if (!isNewChart) return previewKeyRef.current;
    if (!dashboardId) return null;
    if (previewKeyRef.current) return previewKeyRef.current;

    const dvId = chart.datasetViewId;
    if (!dvId) return null;
    const { previewId } = await chartsService.createChartPreview(dvId, dashboardId);
    previewKeyRef.current = previewId;
    return previewId;
  }, [isNewChart, dashboardId, chart.datasetViewId]);

  // Clean up active stream on unmount
  useEffect(() => {
    return () => {
      abortStreamRef.current?.();
    };
  }, []);

  // ── Suggestion visibility (separate state for each flow) ─────────────

  // New Chart flow: shown by default, togglable, hides on first pick
  const hasNewChartSuggestions = !!(chartSuggestions?.newChartSuggestions?.length);
  const [showNewChartSuggestions, setShowNewChartSuggestions] = useState(!!isNewChart);

  // Edit Chart flow: hidden by default, shown only when chat history is empty,
  // permanently hidden once user picks a suggestion or sends any message
  const hasEditSuggestions = !!(editSuggestions?.length);
  const [showEditSuggestions, setShowEditSuggestions] = useState(false);

  const suggestionsSectionRef = useRef<HTMLDivElement>(null);

  // Ref to break circular dependency: suggestion handlers need triggerAiStream
  // but are defined before it. The ref is updated after triggerAiStream is created.
  const triggerAiStreamRef = useRef<(prompt: string, suggestion?: AiEditSuggestion) => Promise<void>>(
    async () => {}
  );

  // Handle clicking a NEW CHART suggestion card — send rich prompt to AI chat
  // Same flow as edit suggestions: the prompt is executed by chart_edit skill
  const handleNewChartSuggestionClick = useCallback((suggestion: NewChartSuggestionItem) => {
    setShowNewChartSuggestions(false);
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}-${messageIdCounter.current++}`,
      role: 'user',
      content: suggestion.title,
    };
    setMessages(prev => [...prev, userMessage]);

    // Build suggestion payload with the rich prompt
    const suggestionPayload: AiEditSuggestion = {
      title: suggestion.title,
      description: suggestion.description,
      reason: suggestion.reason || '',
      chart_type: suggestion.chart_type || 'bar',
      prompt: suggestion.prompt,
      suggestion_type: 'new_chart',
    };

    triggerAiStreamRef.current(suggestion.prompt, suggestionPayload);
  }, []);

  // Handle clicking an EDIT suggestion card — send prompt text to AI chat
  // Hides suggestions permanently (only shown when chat is empty)
  const handleEditSuggestionClick = useCallback((suggestion: EditChartSuggestionItem) => {
    setShowEditSuggestions(false);
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}-${messageIdCounter.current++}`,
      role: 'user',
      content: suggestion.prompt,
    };
    setMessages(prev => [...prev, userMessage]);
    const editSuggestionPayload: AiEditSuggestion = {
      title: suggestion.title,
      description: suggestion.description,
      prompt: suggestion.prompt,
      suggestion_type: 'chart_edit',
    };
    triggerAiStreamRef.current(suggestion.prompt, editSuggestionPayload);
  }, []);

  // Load persisted chat history on mount, then show greeting
  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      // For new charts, show a short greeting (suggestions rendered as cards below)
      if (isNewChart) {
        if (cancelled) return;
        setHistoryLoaded(true);
        const greeting = chartSuggestions?.newChartSuggestions?.length
          ? "Pick a suggestion or describe the chart you'd like to create."
          : "What kind of chart would you like to create?";
        fullBufferRef.current = greeting;
        visibleBufferRef.current = '';
        setIsStreaming(true);
        return;
      }

      try {
        const history = await getChatHistory(chart.id, 50);
        if (cancelled) return;

        if (history.length > 0) {
          // History exists — restore messages, keep edit suggestions hidden
          const restoredMessages: ChatMessage[] = history.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
          }));
          setMessages(restoredMessages);
          setHistoryLoaded(true);
        } else {
          setHistoryLoaded(true);
          // No history — show edit suggestions if available
          if (hasEditSuggestions) {
            setShowEditSuggestions(true);
          }
          // Show initial greeting
          const greeting = "Hi! How can I help with this chart?";
          fullBufferRef.current = greeting;
          visibleBufferRef.current = '';
          setIsStreaming(true);
        }
      } catch (err) {
        console.warn('Failed to load chat history:', err);
        if (cancelled) return;
        setHistoryLoaded(true);
        // Fallback greeting on error
        const greeting = "Hi! How can I help with this chart?";
        fullBufferRef.current = greeting;
        visibleBufferRef.current = '';
        setIsStreaming(true);
      }
    };

    loadHistory();
    return () => { cancelled = true; };
  }, [chart.id]);

  // Custom typing effect loop
  useEffect(() => {
    if (!isStreaming) return;

    const interval = setInterval(() => {
      const full = fullBufferRef.current;
      const visible = visibleBufferRef.current;

      if (visible.length < full.length) {
        // Reveal 3 chars per tick (~100 chars/sec at 40ms interval)
        const next = full.slice(0, visible.length + 3);

        visibleBufferRef.current = next;
        setVisibleBuffer(next);
      } else if (visible.length === full.length && full.length > 0) {
        // Typing complete - commit to history
        setMessages(prev => [...prev, {
          id: `assistant-${Date.now()}-${messageIdCounter.current++}`,
          role: 'assistant',
          content: full
        }]);

        // Clear buffers
        fullBufferRef.current = '';
        visibleBufferRef.current = '';
        setVisibleBuffer('');

        if (!abortStreamRef.current) {
          // No active job (greeting only) — stop streaming
          setIsStreaming(false);
        } else {
          // Active job: brief pause before showing next system message
          setIsLoading(false);
          setTimeout(() => {
            if (abortStreamRef.current) {
              setIsLoading(true);
            }
          }, 500);
        }
      }
    }, 40); // 40ms interval

    return () => clearInterval(interval);
  }, [isStreaming, visibleBuffer]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages, visibleBuffer, isLoading, systemMessage]);

  // Auto-grow textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [promptText]);

  // ── Core AI stream trigger (shared by handleSend and suggestion clicks) ──

  const triggerAiStream = useCallback(async (prompt: string, suggestion?: AiEditSuggestion): Promise<void> => {
    // Small delay to ensure React processes state updates before starting stream
    await new Promise(resolve => setTimeout(resolve, 10));

    setIsStreaming(true);
    setIsLoading(true);
    setSystemMessage(suggestion ? `Applying: ${suggestion.title}…` : 'Thinking…');

    // Lazily create Redis preview for new charts (first AI interaction)
    let previewKey: string | null = null;
    try {
      previewKey = await ensurePreviewKey();
    } catch (err) {
      console.error('Failed to create chart preview:', err);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}-${messageIdCounter.current++}`,
        role: 'assistant',
        content: '❌ Failed to initialize chart preview. Please try again.',
      }]);
      setIsStreaming(false);
      setIsLoading(false);
      setSystemMessage('');
      return;
    }

    const history = messages.map(msg => ({
      role: msg.role,
      content: msg.content,
    }));

    const request: AiEditRequest = {
      prompt,
      history,
      ...(suggestion ? { suggestion } : {}),
      ...(previewKey ? { preview_chart_id: previewKey } : {}),
      ...(filters && Object.keys(filters).length > 0 ? { filters } : {}),
    };

    const handlers: AiEditHandlers = {
      onJobStarted: () => {
        // Job started — keep showing "Thinking…" until backend sends narration
      },

      onProgress: (_stage, _progress, message) => {
        setSystemMessage(message);
        setIsLoading(true);
        setIsStreaming(true);
      },

      onAiTextDelta: (delta) => {
        if (fullBufferRef.current === '') {
          // First delta arrived — clear system message, start typing
          setSystemMessage('');
          setIsLoading(false);
          setIsStreaming(true);
        }
        fullBufferRef.current += delta;
      },

      onAiTextComplete: () => {
        // Full text received (non-streaming fallback)
      },

      onChartDiff: () => {
        const notification = '\n\n✅ Chart updated with new configuration';
        setSystemMessage('');
        setIsLoading(false);
        fullBufferRef.current += notification;
      },

      onJobCompleted: (result) => {
        // Clear abort ref so typing loop knows to stop streaming when done
        abortStreamRef.current = null;

        if (result?.type === 'chart_preview' && result?.preview_key) {
          // Update cached preview key so subsequent AI interactions use the
          // latest preview (which has valid SQL/config), not the initial empty one.
          previewKeyRef.current = result.preview_key;
          if (onPreviewUpdate) {
            onPreviewUpdate(result.preview_key, result.reason || '');
          }
        }
        textareaRef.current?.focus();
      },

      onError: (message) => {

        // Clear all streaming state
        fullBufferRef.current = '';
        visibleBufferRef.current = '';
        setVisibleBuffer('');
        setSystemMessage('');
        setIsLoading(false);
        setIsStreaming(false);

        setMessages(prev => [
          ...prev,
          {
            id: `error-${Date.now()}-${messageIdCounter.current++}`,
            role: 'assistant',
            content: `❌ Error: ${message}`
          },
        ]);
      },

      onJobCancelled: () => {
        const finalMessage = fullBufferRef.current;

        // Clear all streaming state
        fullBufferRef.current = '';
        visibleBufferRef.current = '';
        setVisibleBuffer('');
        setSystemMessage('');
        setIsLoading(false);
        setIsStreaming(false);

        if (finalMessage) {
          setMessages(prev => [
            ...prev,
            {
              id: `cancelled-${Date.now()}-${messageIdCounter.current++}`,
              role: 'assistant',
              content: finalMessage + '\n\n[Cancelled]'
            },
          ]);
        }
      },
    };

    try {
      const abortFn = await streamAiEdit(chart.id, request, handlers);
      abortStreamRef.current = abortFn;
    } catch (error) {
      console.error('Failed to start AI edit stream:', error);
      setMessages(prev => [
        ...prev,
        {
          id: `error-${Date.now()}-${messageIdCounter.current++}`,
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.'
        },
      ]);
      fullBufferRef.current = '';
      visibleBufferRef.current = '';
      setVisibleBuffer('');
      setSystemMessage('');
      setIsStreaming(false);
      setIsLoading(false);
    }
  }, [messages, chart.id, onPreviewUpdate, ensurePreviewKey, filters]);

  // Keep ref in sync so suggestion handlers always call the latest version
  triggerAiStreamRef.current = triggerAiStream;

  // ── handleSend (user-typed messages) ─────────────────────────────────────

  const handleSend = useCallback(async () => {
    const trimmed = promptText.trim();
    if (!trimmed || isStreaming) return;

    // Hide suggestions once user starts chatting (both flows)
    setShowNewChartSuggestions(false);
    setShowEditSuggestions(false);

    // If there's a current message being displayed, move it to history with interruption marker
    if (fullBufferRef.current) {
      const messageToSave = fullBufferRef.current;
      setMessages(prev => [
        ...prev,
        {
          id: `assistant-${Date.now()}-${messageIdCounter.current++}`,
          role: 'assistant',
          content: messageToSave + '\n\n[Interrupted]'
        }
      ]);
    }

    // Abort current stream if running
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }

    // Clear ALL streaming states BEFORE starting new request
    fullBufferRef.current = '';
    visibleBufferRef.current = '';
    setVisibleBuffer('');
    setSystemMessage('');
    setIsStreaming(false);
    setIsLoading(false);

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}-${messageIdCounter.current++}`,
      role: 'user',
      content: trimmed
    };
    setMessages(prev => [...prev, userMessage]);
    setPromptText('');

    await triggerAiStream(trimmed);
  }, [promptText, isStreaming, triggerAiStream]);

  const handleCancelStream = () => {
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }

    const finalMessage = fullBufferRef.current;

    if (finalMessage) {
      setMessages(prev => [
        ...prev,
        {
          id: `cancelled-${Date.now()}-${messageIdCounter.current++}`,
          role: 'assistant',
          content: finalMessage + '\n\n[Cancelled]'
        },
      ]);
    }

    fullBufferRef.current = '';
    visibleBufferRef.current = '';
    setVisibleBuffer('');
    setSystemMessage('');
    setIsStreaming(false);
    setIsLoading(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = promptText.trim().length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.chatArea} ref={chatAreaRef}>
        {messages.map((message) =>
          message.role === 'assistant' ? (
            <div key={message.id} className={styles.messageRow}>
              <div className={styles.assistantMessage}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{fixMarkdownLists(message.content)}</ReactMarkdown>
              </div>
            </div>
          ) : (
            <div key={message.id} className={styles.userMessageRow}>
              <div className={styles.userBubble}>
                <Body1Strong>{message.content}</Body1Strong>
              </div>
            </div>
          )
        )}

        {/* New chart suggestion cards */}
        {isNewChart && showNewChartSuggestions && !isStreaming && chartSuggestions?.newChartSuggestions?.length ? (
          <div className={styles.suggestionsSection} ref={suggestionsSectionRef}>
            {chartSuggestions.newChartSuggestions.map((s, i) => (
              <div
                key={i}
                className={styles.suggestionCard}
                onClick={() => handleNewChartSuggestionClick(s)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleNewChartSuggestionClick(s)}
              >
                <div className={styles.suggestionContent}>
                  <div className={styles.suggestionTitle}>{s.title}</div>
                  <Caption1 className={styles.suggestionDescription}>{s.description}</Caption1>
                </div>
                <Button
                  className={styles.suggestionAddButton}
                  appearance="subtle"
                  size="small"
                  icon={<AddRegular />}
                  aria-label={`Add ${s.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNewChartSuggestionClick(s);
                  }}
                />
              </div>
            ))}
          </div>
        ) : null}

        {/* Edit chart suggestion cards (separate visibility) */}
        {!isNewChart && showEditSuggestions && !isStreaming && editSuggestions?.length ? (
          <div className={styles.suggestionsSection} ref={suggestionsSectionRef}>
            {editSuggestions.map((s, i) => (
              <div
                key={i}
                className={styles.suggestionCard}
                onClick={() => handleEditSuggestionClick(s)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleEditSuggestionClick(s)}
              >
                <div className={styles.suggestionContent}>
                  <div className={styles.suggestionTitle}>{s.title}</div>
                  <Caption1 className={styles.suggestionDescription}>{s.description}</Caption1>
                </div>
                <Button
                  className={styles.suggestionAddButton}
                  appearance="subtle"
                  size="small"
                  icon={<SparkleRegular />}
                  aria-label={`Try: ${s.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditSuggestionClick(s);
                  }}
                />
              </div>
            ))}
          </div>
        ) : null}

        {(isStreaming || visibleBuffer) && (
          <>
            {visibleBuffer && (
              <div className={styles.messageRow}>
                <div className={styles.assistantMessage}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{fixMarkdownLists(visibleBuffer)}</ReactMarkdown>
                </div>
              </div>
            )}

            {isLoading && systemMessage && (
              <div className={styles.messageRow}>
                <div className={styles.assistantMessage}>
                  <Body1 className={styles.progressText}>{systemMessage}</Body1>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Toggle suggestions button — available for both flows */}
      {((isNewChart && hasNewChartSuggestions) || (!isNewChart && hasEditSuggestions)) && !isStreaming && (
        <div style={{ padding: '0 12px' }}>
          <Button
            className={styles.showSuggestionsButton}
            appearance="transparent"
            size="small"
            icon={<SparkleRegular />}
            onClick={() => {
              const setter = isNewChart ? setShowNewChartSuggestions : setShowEditSuggestions;
              setter(prev => {
                const next = !prev;
                if (next) {
                  requestAnimationFrame(() => {
                    suggestionsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  });
                }
                return next;
              });
            }}
          >
            {(isNewChart ? showNewChartSuggestions : showEditSuggestions)
              ? 'Hide suggestions'
              : 'Show suggestions'}
          </Button>
        </div>
      )}

      <div className={styles.composerWrapper}>
        <div className={styles.composerSurface}>
          {isStreaming ? (
            <Button
              className={styles.sendButton}
              appearance="subtle"
              icon={<DismissCircleRegular />}
              aria-label="Cancel"
              onClick={handleCancelStream}
            />
          ) : (
            <>
              <textarea
                ref={textareaRef}
                className={styles.textarea}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Message the chart AI…"
                disabled={isStreaming}
                rows={1}
              />
              <Button
                className={styles.sendButton}
                appearance="transparent"
                icon={<SendRegular />}
                aria-label="Send"
                disabled={!canSend || isStreaming}
                onClick={handleSend}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Wrap with React.memo to prevent re-renders when previewChart changes
// Only re-render if the original chart.id or onPreviewUpdate callback changes
export default React.memo(AIEditor, (prevProps, nextProps) => {
  return (
    prevProps.chart.id === nextProps.chart.id &&
    prevProps.onPreviewUpdate === nextProps.onPreviewUpdate &&
    prevProps.isNewChart === nextProps.isNewChart &&
    prevProps.dashboardId === nextProps.dashboardId &&
    prevProps.previewChartId === nextProps.previewChartId &&
    prevProps.filters === nextProps.filters &&
    prevProps.chartSuggestions === nextProps.chartSuggestions &&
    prevProps.editSuggestions === nextProps.editSuggestions
  );
  // Note: previewChart changes are intentionally ignored to preserve chat state
});
